import { supabase } from './supabase';
import { fetchAll } from './save';
import { TEMPLATE_STAMP } from './templateStamp';
import { decodeCatalogBytes, stripToHeader, unwrapExcel } from './catalogParse';
import { runMatchingPass } from './matchingPass';

/*
  The ten kinds of file OnHand takes in, defined once.

  **Ten, counted on 8 September 2026 rather than carried forward.** This comment
  said "seven" while the list below held nine, and the build order said "eight".
  A count in this project is only true if somebody counted on the day they wrote
  it, and this is the seventh time that has caught something. Nine became ten
  when "Who covers what" was added.

  Written 6 September 2026 for "OnHand's own templates, and an import that says
  where it is going".

  **Each of them used to be written out twice**: once as a line of text for
  the Template button, and once as a loop that read a row and wrote it. The two
  drifted, which is how the Users template offered a Region column that the
  loading code has never done anything with. One definition now serves both, so
  a template cannot offer a column nothing reads.

  **And each one can now hand back what is already here.** That is what makes a
  template worth having, in the build order's own words: export what is there,
  change fifty prices, load it back, and only those fifty change.

  Each definition says:

  - `columns`   what the file carries, and how to write one out of a row
                already in the system
  - `parse`     how to get rows out of the file, which differs wildly: one is
                UTF-16 with a nine-line preamble, one is tab separated
  - `read`      one row of the file to something to write, or a reason it cannot
                be read
  - `existing`  what is already here, keyed the same way as `read`
  - `apply`     writing the rows a person has agreed to
*/

const txt = v => (v === null || v === undefined ? '' : String(v));
const trimmed = v => txt(v).trim();
const nullable = v => trimmed(v) || null;
const num = v => { const n = parseFloat(txt(v).replace(/[$,]/g, '')); return Number.isNaN(n) ? null : n; };
const yes = v => trimmed(v).toUpperCase() === 'TRUE';
const first = (row, ...names) => { for (const n of names) if (row[n] !== undefined) return row[n]; return ''; };
/* A semicolon-separated cell: "Dallas;Houston" to two names, blanks dropped. */
const semis = v => txt(v).split(';').map(x => x.trim()).filter(Boolean);
/* How a location is named in a file: its number if it has one, else its name. */
const label = w => (w ? (w.warehouse_number || w.name) : null);

/* One CSV cell, quoted so a comma or a quote mark inside it survives. */
function cell(v) {
  return '"' + txt(v).replace(/"/g, '""') + '"';
}

/*
  A template file: the stamp line, the headings, and then either one example row
  or every row already in the system.

  **The stamp is the first line of every template OnHand hands out** and says
  what the file is for and which layout it uses, so a file loaded into the wrong
  box can be told apart from a file with a typo in it.
*/
export function buildTemplate(def, rows) {
  const head = TEMPLATE_STAMP + ',' + def.type + ',' + def.version
    + (def.stampNote ? ' - ' + def.stampNote : '');
  const headings = def.columns.map(c => cell(c.header)).join(',');
  const body = (rows && rows.length)
    ? rows.map(r => def.columns.map(c => cell(c.from ? c.from(r) : r[c.key])).join(','))
    : [def.columns.map(c => cell(c.example ?? '')).join(',')];
  return [head, headings, ...body].join('\n');
}

export function templateFileName(def, withRows) {
  return def.file + (withRows ? '_Current' : '_Template') + '_' + def.version + '.csv';
}

/* Reading a plain comma separated file, which five of the seven are. */
async function parseCsv(file) {
  const { default: Papa } = await import('papaparse');
  const text = await file.text();
  // Anything above the headings, including our own stamp line, is not a row.
  const body = text.split('\n').filter(l => !l.trimStart().startsWith('#')).join('\n');
  return Papa.parse(body, { header: true, skipEmptyLines: true }).data;
}

/*
  The two catalog files are not CSV in any ordinary sense. **The ClaimSoft export
  is UTF-16 with a nine line company preamble above the headings; the Account
  Edge one is latin1 and separated by tabs.** Guessing either wrong produces one
  enormous column and no error.
*/
async function parseCatalog(file, delimiter) {
  const { default: Papa } = await import('papaparse');
  const text = stripToHeader(decodeCatalogBytes(await file.arrayBuffer()));
  return Papa.parse(text, { header: true, skipEmptyLines: true, ...(delimiter ? { delimiter: separatorOf(text, delimiter) } : {}) }).data;
}

/*
  **Tabs or commas, whichever the file actually uses** (the audit after the
  rebrand, issue 16, 18 September 2026). Account Edge's own export is split by
  tabs, so its loader read tabs; the template OnHand hands out for it is split by
  commas, like every template, so OnHand's own file could not be loaded back.
  The heading line says which: a heading line with no tab in it and a comma in
  it is a comma file.
*/
export function separatorOf(text, expected) {
  const heading = (text || '').split(/\r?\n/, 1)[0] || '';
  if (expected === '\t' && !heading.includes('\t') && heading.includes(',')) return ',';
  return expected;
}

/*
  The ordinary way to write rows: upsert in batches, and **count what the
  database actually took**. Every one of these used to add the size of the batch
  to a running total whether the write succeeded or not, so a failed import
  could report a confident number.

  **Matched on company plus name** (the audit after the rebrand, issue 16, 18
  September 2026). It matched on the name alone, and the database's rule is
  that a name is unique within a company, so the database had no rule to match
  against and refused every row: "there is no unique or exclusion constraint
  matching the ON CONFLICT specification". Proved on the live database, undone.
  The company comes from the site the person is in, which the database fills
  in by itself.
*/
async function upsertAll(table, payloads, onConflict, size = 200) {
  let written = 0;
  const failed = [];
  for (let i = 0; i < payloads.length; i += size) {
    const slice = payloads.slice(i, i + size);
    const { data, error } = await supabase.from(table).upsert(slice.map(p => p.payload), { onConflict })
      .select('id');
    if (error) {
      for (const p of slice) failed.push({ line: p.line, why: error.message });
    } else {
      written += (data || []).length;
    }
  }
  return { written, failed };
}

export const TEMPLATES = [
  {
    key: 'regions',
    label: 'Regions',
    says: 'The territories a location sits in. Load these first: nothing else in OnHand can create one.',
    noun: 'region',
    type: 'regions', version: 'v1', file: 'OnHand_Regions',
    /*
      **Nothing anywhere in OnHand creates a region.** No screen inserts one, no
      import made one, and the six that exist in the testing site were put there
      by the script that built the test bench. MedEx has none.

      That matters because a region is what a manager covers and what a
      location's name prefix resolves to. Loading the real business into an
      empty company without this would give every location a prefix pointing at
      nothing and every manager an empty screen. Found 6 September 2026 at job
      15, which is the job that loads a company from nothing.
    */
    /* A region is a name and nothing else. The prefix a location carries lives
       on the location, worked out from its own name. */
    columns: [
      { header: 'Region', example: 'Dallas', from: r => r.name },
    ],
    parse: parseCsv,
    read(row) {
      const name = trimmed(first(row, 'Region', 'region', 'Name', 'name'));
      if (!name) return { error: 'no region name' };
      return {
        key: name.toUpperCase(),
        payload: { name },
      };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('regions').select('*').order('name'));
      return new Map((data || []).map(r => [r.name.toUpperCase(), r]));
    },
    apply: plan => upsertAll('regions', [...plan.create, ...plan.update], 'org_id,name'),
  },

  {
    key: 'referrals',
    label: 'Referral sources',
    says: 'The practices a location is paired with. Load these before the locations.',
    noun: 'referral source',
    type: 'referral_sources', version: 'v1', file: 'OnHand_ReferralSources',
    columns: [
      { header: 'Name', example: 'DFW-ASPEN GROVE ORTHOPEDICS', from: r => r.name },
      { header: 'SpecialtyTaxonomy', example: '', from: r => r.specialty_taxonomy },
      { header: 'AddressLine1', example: '', from: r => r.address_line1 },
      { header: 'AddressLine2', example: '', from: r => r.address_line2 },
      { header: 'City', example: 'Dallas', from: r => r.city },
      { header: 'State', example: 'TX', from: r => r.state },
      { header: 'ZIP', example: '', from: r => r.zip },
      { header: 'Phone', example: '', from: r => r.phone },
      { header: 'Fax', example: '', from: r => r.fax },
      { header: 'EmailAddress', example: '', from: r => r.email },
      { header: 'NPI', example: '', from: r => r.npi },
      { header: 'EIN', example: '', from: r => r.ein },
      { header: 'ReferralType', example: '', from: r => r.referral_type },
      { header: 'DefaultSalesperson', example: '', from: r => r.default_salesperson },
      { header: 'DefaultPsr', example: '', from: r => r.default_psr },
    ],
    parse: parseCsv,
    read(row) {
      const fullName = trimmed(row['Name']);
      if (!fullName) return { error: 'no name' };
      const { name, display_name, region_prefix } = splitName(fullName);
      return {
        key: name.toUpperCase(),
        payload: {
          name, display_name, region_prefix,
          specialty_taxonomy: nullable(row['SpecialtyTaxonomy']),
          address_line1: nullable(row['AddressLine1']), address_line2: nullable(row['AddressLine2']),
          city: nullable(row['City']), state: nullable(row['State']), zip: nullable(row['ZIP']),
          phone: nullable(row['Phone']), fax: nullable(row['Fax']), email: nullable(row['EmailAddress']),
          npi: nullable(row['NPI']), ein: nullable(row['EIN']),
          referral_type: nullable(row['ReferralType']),
          default_salesperson: nullable(row['DefaultSalesperson']),
          default_psr: nullable(row['DefaultPsr']),
          updated_at: new Date().toISOString(),
        },
      };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('referral_sources').select('*').order('name'));
      return new Map((data || []).map(r => [r.name.toUpperCase(), r]));
    },
    // Company plus name: see the note on upsertAll.
    apply: plan => upsertAll('referral_sources', [...plan.create, ...plan.update], 'org_id,name'),
  },

  {
    key: 'warehouses',
    label: 'Warehouses',
    says: 'Where stock lives: the name, the number, the address and who manages it.',
    noun: 'location',
    type: 'warehouses', version: 'v1', file: 'OnHand_Warehouses',
    columns: [
      { header: 'Warehouse_Name', example: 'DFW-ASPEN GROVE ORTHOPEDICS', from: r => r.name },
      { header: 'Warehouse_Number', example: 'TB-0001', from: r => r.warehouse_number },
      { header: 'Address1', example: '', from: r => r.address1 },
      { header: 'Address2', example: '', from: r => r.address2 },
      { header: 'City', example: 'Dallas', from: r => r.city },
      { header: 'State', example: 'TX', from: r => r.state },
      { header: 'Zip', example: '', from: r => r.zip },
      { header: 'Phone1', example: '', from: r => r.phone },
      { header: 'PrimaryWarehouse', example: '', from: r => r.primary_warehouse },
      { header: 'WarehouseManagerName', example: '', from: r => r.warehouse_manager },
      { header: 'ShipTo', example: 'FALSE', from: r => (r.ship_to ? 'TRUE' : 'FALSE') },
      { header: 'TransferOnly', example: 'FALSE', from: r => (r.transfer_only ? 'TRUE' : 'FALSE') },
      { header: 'Inactive', example: 'FALSE', from: r => (r.is_inactive ? 'TRUE' : 'FALSE') },
    ],
    parse: parseCsv,
    read(row) {
      const fullName = trimmed(first(row, 'Warehouse_Name', 'Name'));
      if (!fullName) return { error: 'no warehouse name' };
      const { name, display_name, region_prefix } = splitName(fullName);
      return {
        key: name.toUpperCase(),
        payload: {
          name, display_name, region_prefix,
          warehouse_number: nullable(row['Warehouse_Number']),
          address1: nullable(row['Address1']), address2: nullable(row['Address2']),
          city: nullable(row['City']), state: nullable(row['State']), zip: nullable(row['Zip']),
          phone: nullable(row['Phone1']),
          primary_warehouse: nullable(row['PrimaryWarehouse']),
          warehouse_manager: nullable(row['WarehouseManagerName']),
          ship_to: yes(row['ShipTo']),
          transfer_only: yes(row['TransferOnly']),
          is_inactive: yes(row['Inactive']),
          updated_at: new Date().toISOString(),
        },
      };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('warehouses')
        .select('name, display_name, region_prefix, warehouse_number, address1, address2, city, state, zip, phone, primary_warehouse, warehouse_manager, ship_to, transfer_only, is_inactive')
        .order('name'));
      return new Map((data || []).map(r => [r.name.toUpperCase(), r]));
    },
    // Company plus name: see the note on upsertAll.
    apply: plan => upsertAll('warehouses', [...plan.create, ...plan.update], 'org_id,name'),
  },

  {
    key: 'users',
    label: 'People',
    says: 'Names and job levels. It never creates an account, and it does not set what anybody covers.',
    noun: 'person',
    type: 'users', version: 'v1', file: 'OnHand_Users',
    /*
      **This one cannot create anybody and the file says so**, because a person's
      row is keyed to their login and only an administrator on Users & Access can
      make one of those. It changes people who already have one.
    */
    columns: [
      { header: 'EmailAddress', example: 'jsmith@example.com', from: r => r.email },
      { header: 'FirstName', example: 'Jane', from: r => r.first_name },
      { header: 'LastName', example: 'Smith', from: r => r.last_name },
      { header: 'FullName', example: 'Jane Smith', from: r => r.full_name },
      { header: 'Role', example: 'rep', from: r => r.role },
      { header: 'Status', example: 'Active', from: r => (r.is_active ? 'Active' : 'Inactive') },
    ],
    parse: parseCsv,
    read(row) {
      const email = trimmed(first(row, 'EmailAddress', 'email', 'Email'));
      if (!email) return { error: 'no email address' };
      const firstName = trimmed(first(row, 'FirstName', 'first_name'));
      const lastName  = trimmed(first(row, 'LastName', 'last_name'));
      const role      = trimmed(first(row, 'Role', 'role')).toLowerCase() || 'rep';
      if (!['admin', 'manager', 'rep', 'buyer'].includes(role)) {
        return { error: 'no job level called "' + role + '"' };
      }
      const status = trimmed(first(row, 'Status', 'status')) || 'Active';
      return {
        key: email.toLowerCase(),
        payload: {
          email,
          first_name: firstName || null,
          last_name: lastName || null,
          full_name: trimmed(first(row, 'FullName', 'fullname')) || (firstName + ' ' + lastName).trim() || null,
          role,
          is_active: status.toLowerCase() === 'active',
        },
      };
    },
    async existing() {
      const { data } = await supabase.rpc('site_users');
      return new Map((data || []).filter(p => p.email)
        .map(p => [p.email.trim().toLowerCase(), {
          email: p.email, first_name: p.first_name, last_name: p.last_name,
          full_name: p.full_name, role: p.role, is_active: p.is_active,
        }]));
    },
    /*
      **A person who has no login cannot be created here, and that is reported
      as an unreadable row rather than as a failure**, so the message names them
      and says what to do next. Before 5 September 2026 this import failed on
      every row and told nobody why.
    */
    unknownIsError: 'no account yet. Add them on Users & Access first',
    async apply(plan, ctx) {
      const failed = [];
      let written = 0;
      for (const row of plan.update) {
        const person = ctx.people.get(row.key);
        const p = row.payload;
        const { error: nameErr } = await supabase.from('profiles').update({
          full_name: p.full_name || person.full_name,
          first_name: p.first_name || person.first_name,
          last_name: p.last_name || person.last_name,
        }).eq('id', person.id);
        if (nameErr) { failed.push({ line: row.line, why: nameErr.message }); continue; }
        if (p.role !== person.role) {
          const { error } = await supabase.rpc('set_membership',
            { target_user: person.id, target_org: ctx.orgId, new_role: p.role, active: true });
          if (error) { failed.push({ line: row.line, why: error.message }); continue; }
        }
        if (p.is_active !== person.is_active) {
          const { error } = await supabase.rpc('set_user_active', { target_user: person.id, value: p.is_active });
          if (error) { failed.push({ line: row.line, why: error.message }); continue; }
        }
        written += 1;
      }
      return { written, failed };
    },
  },

  /*
    **Who covers what, in one file.** Added 8 September 2026, because the owner
    asked for it in the same breath as the region picker: *"I will also assign by
    csv file upload for the initial data setup."* Setting up twelve people across
    320 locations by hand is 300 ticks.

    **It is the same shape as the screen**, which is the only reason it is worth
    having: a region, and then the exceptions.

      Regions    the regions this person covers, separated by semicolons. Every
                 location in them is theirs, and a location opened in one of
                 them later reaches them on its own.
      Locations  anything extra, outside those regions, by warehouse number or
                 by name.
      Except     locations inside those regions that this person does NOT cover.

    **Hand it back out and it comes back the same.** Downloading the current
    file writes each person's coverage in exactly those three columns, so the
    round trip holds: export, change one person, load it back, and only that
    person changes.

    **A blank Regions column and a blank Locations column means "covers
    nothing"**, and it is treated as a real instruction rather than as a row to
    skip, because the alternative is a file that can give somebody locations and
    can never take them away.
  */
  {
    key: 'assignments',
    label: 'Who covers what',
    says: 'Which regions and which locations each person covers. It never creates an account.',
    noun: 'person',
    type: 'assignments', version: 'v1', file: 'OnHand_WhoCoversWhat',
    stampNote: 'regions, then the exceptions',
    columns: [
      { header: 'EmailAddress', example: 'jsmith@example.com', from: r => r.email },
      { header: 'FullName', example: 'Jane Smith', from: r => r.full_name },
      { header: 'Regions', example: 'Dallas;Houston', from: r => (r.regions || []).join(';') },
      { header: 'Locations', example: 'TB-0001;TB-0002', from: r => (r.locations || []).join(';') },
      { header: 'Except', example: '', from: r => (r.except || []).join(';') },
    ],
    parse: parseCsv,
    read(row) {
      const email = trimmed(first(row, 'EmailAddress', 'email', 'Email'));
      if (!email) return { error: 'no email address' };
      return {
        key: email.toLowerCase(),
        payload: {
          regions:   semis(first(row, 'Regions', 'regions')),
          locations: semis(first(row, 'Locations', 'locations')),
          except:    semis(first(row, 'Except', 'except')),
        },
      };
    },
    /*
      **Read back the same way it is written.** A person's coverage is stored as
      a plain list of locations, so this turns that list back into regions and
      exceptions: a region they hold is a region, a location they hold outside
      those regions is an extra, and a location inside one of those regions that
      they do NOT hold is an exception.
    */
    async existing() {
      const [{ data: people }, { data: regions }, { data: warehouses }, { data: held }, { data: heldRegions }] =
        await Promise.all([
          supabase.rpc('site_users'),
          supabase.from('regions').select('id, name'),
          fetchAll(() => supabase.from('warehouses').select('id, name, warehouse_number, region_id')),
          fetchAll(() => supabase.from('user_warehouses').select('user_id, warehouse_id')),
          supabase.from('user_regions').select('user_id, region_id'),
        ]);

      const regionName = new Map((regions || []).map(r => [r.id, r.name]));
      const byId = new Map((warehouses || []).map(w => [w.id, w]));
      const inRegion = new Map();
      for (const w of warehouses || []) {
        if (!w.region_id) continue;
        if (!inRegion.has(w.region_id)) inRegion.set(w.region_id, []);
        inRegion.get(w.region_id).push(w.id);
      }

      const theirRegions = new Map();
      for (const r of heldRegions || []) {
        if (!theirRegions.has(r.user_id)) theirRegions.set(r.user_id, []);
        theirRegions.get(r.user_id).push(r.region_id);
      }
      const theirPlaces = new Map();
      for (const r of held || []) {
        if (!theirPlaces.has(r.user_id)) theirPlaces.set(r.user_id, new Set());
        theirPlaces.get(r.user_id).add(r.warehouse_id);
      }

      const out = new Map();
      for (const p of people || []) {
        if (!p.email) continue;
        const mine = theirPlaces.get(p.id) || new Set();
        const regionIds = theirRegions.get(p.id) || [];
        const covered = new Set();
        const except = [];
        for (const rid of regionIds) {
          for (const wid of inRegion.get(rid) || []) {
            covered.add(wid);
            if (!mine.has(wid)) except.push(label(byId.get(wid)));
          }
        }
        const extras = [...mine].filter(wid => !covered.has(wid)).map(wid => label(byId.get(wid)));
        out.set(p.email.trim().toLowerCase(), {
          email: p.email, full_name: p.full_name,
          regions: regionIds.map(r => regionName.get(r)).filter(Boolean).sort(),
          locations: extras.filter(Boolean).sort(),
          except: except.filter(Boolean).sort(),
        });
      }
      return out;
    },
    unknownIsError: 'no account yet. Add them on Users & Access first',
    async apply(plan, ctx) {
      const [{ data: regions }, { data: warehouses }] = await Promise.all([
        supabase.from('regions').select('id, name'),
        fetchAll(() => supabase.from('warehouses').select('id, name, warehouse_number, region_id')),
      ]);
      const regionByName = new Map((regions || []).map(r => [r.name.trim().toUpperCase(), r.id]));
      const placeBy = new Map();
      for (const w of warehouses || []) {
        if (w.warehouse_number) placeBy.set(w.warehouse_number.trim().toUpperCase(), w);
        if (w.name) placeBy.set(w.name.trim().toUpperCase(), w);
      }

      const failed = [];
      let written = 0;
      for (const row of plan.update) {
        const person = ctx.people.get(row.key);
        const p = row.payload;

        /*
          **A name nobody recognises stops that person's row.** Silently
          skipping a region called "Dalls" would set somebody up with a third of
          their locations and report success, which is the shape of fault this
          project keeps writing down.
        */
        const regionIds = [];
        let bad = null;
        for (const name of p.regions) {
          const id = regionByName.get(name.toUpperCase());
          if (!id) { bad = 'no region called "' + name + '"'; break; }
          regionIds.push(id);
        }
        const place = name => {
          const w = placeBy.get(name.toUpperCase());
          if (!w) { bad = bad || ('no location called "' + name + '"'); return null; }
          return w;
        };
        const extras = p.locations.map(place);
        const excepts = p.except.map(place);
        if (bad) { failed.push({ line: row.line, why: bad }); continue; }

        const excluded = new Set(excepts.map(w => w.id));
        const ids = new Set(extras.map(w => w.id));
        for (const w of warehouses || []) {
          if (regionIds.includes(w.region_id) && !excluded.has(w.id)) ids.add(w.id);
        }

        const { error } = await supabase.rpc('set_user_access', {
          target_user: person.id,
          region_ids: regionIds,
          warehouse_ids: [...ids],
        });
        if (error) { failed.push({ line: row.line, why: error.message }); continue; }
        written += 1;
      }
      return { written, failed };
    },
  },

  {
    key: 'vendor_aliases',
    label: 'Vendor aliases',
    says: 'Every spelling of a vendor name, and the one official name it means. Load this before the catalogs.',
    noun: 'spelling',
    type: 'vendor_aliases', version: 'v1', file: 'OnHand_VendorAliases',
    /*
      **This is the file the whole crosswalk rests on and OnHand has never had a
      way to load it.** Two items are the same product when their vendors
      provably agree, and agreeing means both spellings resolve to one official
      name through this list. With no list, nothing can ever be confirmed.
    */
    columns: [
      { header: 'Alias', example: 'DJ Orthopedics LLC', from: r => r.alias },
      { header: 'CanonicalName', example: 'DJO', from: r => r.canonical_name },
      { header: 'SourceSystem', example: 'claimsoft', from: r => r.source_system },
    ],
    parse: parseCsv,
    read(row) {
      const alias = trimmed(first(row, 'Alias', 'alias'));
      const canonical = trimmed(first(row, 'CanonicalName', 'canonical_name', 'Canonical'));
      if (!alias) return { error: 'no vendor spelling' };
      if (!canonical) return { error: 'no official name for "' + alias + '"' };
      return {
        key: alias.toUpperCase(),
        payload: {
          alias, canonical_name: canonical,
          source_system: nullable(first(row, 'SourceSystem', 'source_system')),
          updated_at: new Date().toISOString(),
        },
      };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('vendor_aliases')
        .select('alias, canonical_name, source_system').order('alias'));
      return new Map((data || []).map(r => [r.alias.toUpperCase(), r]));
    },
    async apply(plan) {
      const out = await upsertAll('vendor_aliases', [...plan.create, ...plan.update], 'org_id,alias');
      out.note = await matchNote();
      return out;
    },
  },

  {
    key: 'accounts',
    label: 'Accounts',
    says: 'Account name, region, whether it is open, and which catalog it orders from.',
    noun: 'account',
    type: 'accounts', version: 'v1', file: 'OnHand_Accounts',
    columns: [
      { header: 'Account', example: 'Example Account', from: r => r.name },
      { header: 'Region', example: 'Dallas', from: r => r.region_name },
      { header: 'Status', example: 'Open', from: r => (r.is_active ? 'Open' : 'Closed') },
      { header: 'Rep', example: 'Jane Smith', from: r => r.rep_name_raw },
      { header: 'Item Catalog', example: 'edge', from: r => r.catalog_source },
    ],
    parse: parseCsv,
    read(row) {
      const name = trimmed(first(row, 'Account', 'account'));
      if (!name) return { error: 'no account name' };
      return {
        key: name.toUpperCase(),
        payload: {
          name,
          // A blank Region is left out of the row altogether, so the preview
          // does not call it a change and the load does not clear anything
          // (question 19, 18 September 2026: blank means no change).
          ...(trimmed(first(row, 'Region', 'region')) ? { region_name: trimmed(first(row, 'Region', 'region')) } : {}),
          is_active: (trimmed(first(row, 'Status', 'status')) || 'Open').toLowerCase() === 'open',
          catalog_source: (trimmed(first(row, 'Item Catalog', 'item_catalog')) || 'edge').toLowerCase(),
          rep_name_raw: nullable(first(row, 'Rep', 'rep')),
        },
      };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('accounts')
        .select('name, is_active, catalog_source, rep_name_raw, region:regions(name)').order('name'));
      return new Map((data || []).map(r => [r.name.toUpperCase(), {
        name: r.name, is_active: r.is_active, catalog_source: r.catalog_source,
        rep_name_raw: r.rep_name_raw, region_name: r.region?.name || null,
      }]));
    },
    /*
      **The region is looked up once, not once per row.** The old version asked
      the database for the region on every single line of the file.
    */
    async apply(plan) {
      const { data: regions } = await supabase.from('regions').select('id, name');
      const byName = new Map((regions || []).map(r => [r.name.trim().toLowerCase(), r.id]));
      const rows = [];
      const failed = [];
      for (const row of [...plan.create, ...plan.update]) {
        const wanted = row.payload.region_name;
        const regionId = wanted ? byName.get(wanted.toLowerCase()) : null;
        if (wanted && !regionId) { failed.push({ line: row.line, why: 'no region called "' + wanted + '"' }); continue; }
        const { region_name, ...rest } = row.payload;
        // **A blank Region leaves the account's region as it is**, the owner's
        // answer of 18 September 2026 (question 19). So a row with no region
        // does not carry the column at all, and is written in its own batch:
        // one batch has to name the same columns on every row.
        rows.push({ ...row, payload: regionId ? { ...rest, region_id: regionId } : rest });
      }
      const withRegion = rows.filter(r => 'region_id' in r.payload);
      const without = rows.filter(r => !('region_id' in r.payload));
      const a = await upsertAll('accounts', withRegion, 'org_id,name');
      const b = await upsertAll('accounts', without, 'org_id,name');
      return { written: a.written + b.written, failed: [...failed, ...a.failed, ...b.failed] };
    },
  },

  {
    key: 'items_claimsoft',
    label: 'Items (Claimsoft)',
    says: 'The ClaimSoft catalog, straight out of ClaimSoft.',
    noun: 'item',
    type: 'items_claimsoft', version: 'v1', file: 'OnHand_Items_Claimsoft',
    columns: [
      { header: 'ItemNumber', example: '10400-1', from: r => r.item_number },
      { header: 'Description', example: 'CRUTCH - ADULT - 8/CS', from: r => r.description },
      { header: 'VendorName', example: 'Drive Medical', from: r => r.vendor },
      { header: 'VendorPartNumber', example: '', from: r => r.vendor_part_number },
      { header: 'ItemCategory', example: 'Mobility', from: r => r.category },
      { header: 'HCPCS', example: '', from: r => r.hcpcs },
      { header: 'CostPerItem', example: '24.99', from: r => r.cost_per_item },
      { header: 'SellingPrice', example: '', from: r => r.selling_price },
      { header: 'PurchaseUOM', example: 'EA', from: r => r.purchase_uom },
      { header: 'ItemsPerUOM', example: '1', from: r => r.items_per_uom },
    ],
    parse: file => parseCatalog(file),
    read: row => readCatalogRow(row, 'claimsoft'),
    existing: () => existingItems('claimsoft'),
    async apply(plan) {
      const out = await upsertAll('items', [...plan.create, ...plan.update], 'org_id,source,item_number', 500);
      out.note = await matchNote();
      return out;
    },
  },

  {
    key: 'items_edge',
    label: 'Items (Account Edge)',
    says: 'The Account Edge catalog, straight out of Account Edge.',
    noun: 'item',
    type: 'items_account_edge', version: 'v1', file: 'OnHand_Items_AccountEdge',
    columns: [
      { header: 'Item Number', example: 'B-242900001', from: r => r.item_number },
      { header: 'Item Name', example: 'WALKING BOOT - REBOUND AIR - XS', from: r => r.description },
      { header: 'Primary Vendor', example: 'OSSUR', from: r => r.vendor },
      { header: 'Vendor Item Number', example: '', from: r => r.vendor_part_number },
      { header: 'Custom List 1', example: 'Orthopedic', from: r => r.category },
      { header: 'Standard Cost', example: '24.99', from: r => r.cost_per_item },
      { header: 'Selling Price', example: '', from: r => r.selling_price },
      { header: 'Buy Unit Measure', example: 'EA', from: r => r.edge_uom },
      { header: '# Items/Buy Unit', example: '1', from: r => r.edge_items_per_uom },
      { header: 'Inactive Item', example: 'N', from: r => (r.is_inactive ? 'Y' : 'N') },
    ],
    parse: file => parseCatalog(file, '\t'),
    read: row => readCatalogRow(row, 'edge'),
    existing: () => existingItems('edge'),
    async apply(plan) {
      const out = await upsertAll('items', [...plan.create, ...plan.update], 'org_id,source,item_number', 500);
      out.note = await matchNote();
      return out;
    },
  },

  {
    key: 'barcodes',
    label: 'Barcodes',
    says: 'Up to six barcodes against an item number. It never adds an item.',
    noun: 'item',
    type: 'barcodes', version: 'v1', file: 'OnHand_Barcodes',
    stampNote: 'Format barcode columns as Text in Excel to avoid scientific notation',
    columns: [
      { header: 'item_number', example: '10400-1', from: r => r.item_number },
      { header: 'barcode_1', example: '1234567890123', from: r => r.barcode_1 },
      { header: 'barcode_2', example: '', from: r => r.barcode_2 },
      { header: 'barcode_3', example: '', from: r => r.barcode_3 },
      { header: 'barcode_4', example: '', from: r => r.barcode_4 },
      { header: 'barcode_5', example: '', from: r => r.barcode_5 },
      { header: 'barcode_6', example: '', from: r => r.barcode_6 },
    ],
    parse: parseCsv,
    read(row) {
      const number = trimmed(first(row, 'item_number', 'ItemNumber', 'Item Number', 'ITEM_NUMBER', 'itemNumber'));
      if (!number) return { error: 'no item number' };
      const payload = {};
      for (let i = 1; i <= 6; i += 1) {
        const val = trimmed(row['barcode_' + i]);
        if (!val) continue;
        /*
          **Excel turns a long barcode into 5.69097E+12 and there is no way back
          from it.** It was skipped silently before; it is a row a person is told
          about now, with the one thing that fixes it.
        */
        if (/^[\d.]+e[+-]?\d+$/i.test(val)) {
          return { error: 'barcode ' + i + ' reads ' + val + '. Format that column as Text in Excel and save again' };
        }
        payload['barcode_' + i] = val;
      }
      if (!Object.keys(payload).length) return { error: 'no barcodes on this row' };
      return { key: number.toUpperCase(), payload };
    },
    async existing() {
      const { data } = await fetchAll(() => supabase.from('items')
        .select('item_number, barcode_1, barcode_2, barcode_3, barcode_4, barcode_5, barcode_6')
        .order('item_number'));
      return new Map((data || []).map(r => [r.item_number.toUpperCase(), r]));
    },
    unknownIsError: 'no item with that number in the catalog',
    async apply(plan) {
      let written = 0;
      const failed = [];
      for (const row of plan.update) {
        const { data, error } = await supabase.from('items').update(row.payload)
          .eq('item_number', row.raw ?? row.key).select('id');
        if (error) failed.push({ line: row.line, why: error.message });
        else written += (data || []).length;
      }
      return { written, failed };
    },
  },
];

/*
  What was counted before OnHand existed.

  **`historical_counts` is how a new count sheet knows what was there last
  quarter.** Two database functions read it, `open_count_cycle` and
  `populate_count_line_items`, and the number a rep sees beside a line as "last
  count" comes from it. The table is empty: its 82 rows belonged to MedEx and
  went with the clean slate on 26 August 2026.

  **So loading the real business without this makes every first count blind**,
  which is the whole point of the "Load the real business into MedEx" job
  listing prior counts among what it loads.
*/
TEMPLATES.push({
  key: 'prior_counts',
  label: 'Prior counts',
  says: 'What was counted before OnHand. This is where "last count" on a count sheet comes from.',
  noun: 'line',
  type: 'prior_counts', version: 'v1', file: 'OnHand_PriorCounts',
  columns: [
    { header: 'Quarter', example: 'Q4', from: r => r.quarter },
    { header: 'Year', example: '2026', from: r => r.year },
    { header: 'Region', example: 'Dallas', from: r => r.region },
    { header: 'Location', example: 'DFW-ASPEN GROVE ORTHOPEDICS', from: r => r.account_name },
    { header: 'ItemNumber', example: '10400-1', from: r => r.item_number },
    { header: 'Description', example: 'CRUTCH - ADULT - 8/CS', from: r => r.description },
    { header: 'Vendor', example: 'Drive Medical', from: r => r.vendor },
    { header: 'Quantity', example: '14', from: r => r.quantity },
    { header: 'CountDate', example: '2026-10-02', from: r => r.count_date },
    { header: 'CountedBy', example: 'Michelle Chelette', from: r => r.staff_member },
  ],
  parse: parseCsv,
  read(row) {
    const item = trimmed(first(row, 'ItemNumber', 'Item Number', 'item_number'));
    const place = trimmed(first(row, 'Location', 'Account', 'account_name'));
    if (!item) return { error: 'no item number' };
    if (!place) return { error: 'no location' };
    const quarter = trimmed(first(row, 'Quarter', 'quarter'));
    const year = parseInt(trimmed(first(row, 'Year', 'year')), 10);
    if (!quarter) return { error: 'no quarter' };
    if (!Number.isFinite(year)) return { error: 'no year, or a year that is not a number' };
    /*
      **A blank quantity is not a zero**, which is rule 9 and matters more here
      than almost anywhere: this number becomes "last count" on a sheet, so a
      blank read as 0 would tell a rep the shelf was empty last quarter when
      nobody had been to it.
    */
    const qtyRaw = trimmed(first(row, 'Quantity', 'quantity', 'Qty'));
    if (qtyRaw === '') return { error: 'no quantity. A blank is not a zero' };
    const quantity = Number(qtyRaw);
    if (!Number.isFinite(quantity)) return { error: '"' + qtyRaw + '" is not a number' };
    /*
      **A day is built out of its three numbers, never read as a moment.** A
      browser reads "2026-10-02" as midnight in Greenwich, which in Texas is the
      evening before, so the ordinary date reader would store the 1st.
    */
    const dateRaw = trimmed(first(row, 'CountDate', 'count_date', 'Date'));
    let countDate = null;
    if (dateRaw) {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateRaw);
      if (!m) return { error: '"' + dateRaw + '" is not a date. Write it as 2026-10-02' };
      countDate = m[1] + '-' + m[2] + '-' + m[3];
    }
    return {
      key: [quarter, year, place, item].join('|').toUpperCase(),
      payload: {
        quarter, year,
        region: trimmed(first(row, 'Region', 'region')) || 'Unknown',
        account_name: place,
        item_number: item,
        description: trimmed(first(row, 'Description', 'description')) || item,
        vendor: nullable(first(row, 'Vendor', 'vendor')),
        quantity,
        count_date: countDate,
        staff_member: nullable(first(row, 'CountedBy', 'staff_member', 'Staff')),
      },
    };
  },
  async existing() {
    const { data } = await fetchAll(() => supabase.from('historical_counts')
      .select('quarter, year, region, account_name, item_number, description, vendor, quantity, count_date, staff_member')
      .order('item_number'));
    return new Map((data || []).map(r => [
      [r.quarter, r.year, r.account_name, r.item_number].join('|').toUpperCase(), r,
    ]));
  },
  apply: plan => upsertAll('historical_counts', [...plan.create, ...plan.update],
    'org_id,quarter,year,account_name,item_number', 500),
});

export function templateByKey(key) {
  return TEMPLATES.find(t => t.key === key) || null;
}

/*
  A location's name carries its region in front of it: "DFW-ASPEN GROVE
  ORTHOPEDICS" is the Dallas one. The full name is kept as it is, and the two
  halves are stored beside it so a screen can show either.
*/
function splitName(fullName) {
  const m = /^([A-Z]{2,4})-(.+)$/.exec(fullName.trim());
  return m
    ? { name: fullName.trim(), display_name: m[2].trim(), region_prefix: m[1] }
    : { name: fullName.trim(), display_name: fullName.trim(), region_prefix: null };
}

function readCatalogRow(row, source) {
  const number = unwrapExcel(source === 'edge' ? first(row, 'Item Number', 'ItemNumber') : row['ItemNumber'] || '');
  if (!number) return { error: 'no item number' };
  const common = { item_number: number, source, updated_at: new Date().toISOString() };
  const payload = source === 'claimsoft' ? {
    ...common,
    description: nullable(row['Description']),
    vendor: nullable(row['VendorName']),
    vendor_part_number: unwrapExcel(row['VendorPartNumber'] || '') || null,
    vendor_description: nullable(row['VendorDescription']),
    category: nullable(row['ItemCategory']),
    item_type: nullable(row['ItemType']),
    product_family: nullable(row['ProductFamily']),
    size: nullable(row['Size']), side: nullable(row['Side']),
    barcode_1: unwrapExcel(row['Barcode1'] || '') || null,
    barcode_2: unwrapExcel(row['Barcode2'] || '') || null,
    ndc_number: nullable(row['NDCNumber']), hcpcs: nullable(row['HCPCS']),
    mod_1: nullable(row['Mod1']), mod_2: nullable(row['Mod2']),
    mod_3: nullable(row['Mod3']), mod_4: nullable(row['Mod4']),
    cost_per_item: num(row['CostPerItem']), selling_price: num(row['SellingPrice']),
    rental_price: num(row['RentalPrice']), used_price: num(row['UsedPrice']),
    purchase_uom: nullable(row['PurchaseUOM']), items_per_uom: num(row['ItemsPerUOM']),
    lead_time: num(row['LeadTime']), par_level: num(row['ParLevel']),
    min_order_qty: num(row['MinOrderQuantity']),
    is_serialized: yes(row['IsSerialized']), allow_neg_qty: yes(row['AllowNegQty']),
    billable_item: yes(row['BillableItem']), is_taxable: yes(row['IsTaxable']),
    manufacturer: nullable(row['Manufacturer']), location: nullable(row['Location']),
    bin: nullable(row['Bin']), warehouse: nullable(row['Warehouse']),
    cmn: nullable(row['CMN']), discontinue_date: nullable(row['DiscontinueDate']),
    is_inactive: !!trimmed(row['DiscontinueDate']),
    claimsoft_item_number: number,
  } : {
    ...common,
    description: nullable(first(row, 'Item Name', 'Description')),
    vendor: nullable(row['Primary Vendor']),
    vendor_part_number: unwrapExcel(row['Vendor Item Number'] || '') || null,
    category: nullable(row['Custom List 1']),
    custom_field_1: nullable(row['Custom Field 1']),
    custom_field_2: nullable(row['Custom Field 2']),
    custom_field_3: nullable(row['Custom Field 3']),
    cost_per_item: num(row['Standard Cost']), selling_price: num(row['Selling Price']),
    purchase_uom: nullable(row['Buy Unit Measure']), sell_uom: nullable(row['Sell Unit Measure']),
    items_per_uom: num(row['# Items/Buy Unit']),
    edge_uom: nullable(row['Buy Unit Measure']), edge_items_per_uom: num(row['# Items/Buy Unit']),
    par_level: num(row['Minimum Level']), min_order_qty: num(row['Reorder Quantity']),
    is_serialized: trimmed(row['Track Serial Number']).toUpperCase() === 'Y',
    is_inactive: trimmed(row['Inactive Item']).toUpperCase() === 'Y',
    brand: nullable(row['Brand']),
    edge_item_number: number,
  };
  return { key: source + '|' + number.toUpperCase(), payload: onlyWhatTheFileHas(payload, row, CATALOG_COLUMNS[source]) };
}

/*
  **A field is saved only when the file has its column** (the audit after the
  rebrand, issue 16, 18 September 2026). A real ClaimSoft or Account Edge
  export carries every column, so for those nothing changes. OnHand's own
  catalog template carries fewer, and loading it back wrote blank over every
  column it did not have: an item's barcodes, its price, its HCPCS code, gone,
  because the file had no box for them rather than because anybody emptied one.

  Which heading each saved field comes from. A field whose headings are all
  missing from the file is left out of the row, so the database keeps what it
  has; a heading that is in the file and empty still clears the field, because
  that is somebody saying so.
*/
const CATALOG_COLUMNS = {
  claimsoft: {
    description: ['Description'], vendor: ['VendorName'], vendor_part_number: ['VendorPartNumber'],
    vendor_description: ['VendorDescription'], category: ['ItemCategory'], item_type: ['ItemType'],
    product_family: ['ProductFamily'], size: ['Size'], side: ['Side'],
    barcode_1: ['Barcode1'], barcode_2: ['Barcode2'], ndc_number: ['NDCNumber'], hcpcs: ['HCPCS'],
    mod_1: ['Mod1'], mod_2: ['Mod2'], mod_3: ['Mod3'], mod_4: ['Mod4'],
    cost_per_item: ['CostPerItem'], selling_price: ['SellingPrice'], rental_price: ['RentalPrice'],
    used_price: ['UsedPrice'], purchase_uom: ['PurchaseUOM'], items_per_uom: ['ItemsPerUOM'],
    lead_time: ['LeadTime'], par_level: ['ParLevel'], min_order_qty: ['MinOrderQuantity'],
    is_serialized: ['IsSerialized'], allow_neg_qty: ['AllowNegQty'], billable_item: ['BillableItem'],
    is_taxable: ['IsTaxable'], manufacturer: ['Manufacturer'], location: ['Location'], bin: ['Bin'],
    warehouse: ['Warehouse'], cmn: ['CMN'], discontinue_date: ['DiscontinueDate'],
    is_inactive: ['DiscontinueDate'],
  },
  edge: {
    description: ['Item Name', 'Description'], vendor: ['Primary Vendor'],
    vendor_part_number: ['Vendor Item Number'], category: ['Custom List 1'],
    custom_field_1: ['Custom Field 1'], custom_field_2: ['Custom Field 2'], custom_field_3: ['Custom Field 3'],
    cost_per_item: ['Standard Cost'], selling_price: ['Selling Price'],
    purchase_uom: ['Buy Unit Measure'], sell_uom: ['Sell Unit Measure'],
    items_per_uom: ['# Items/Buy Unit'], edge_uom: ['Buy Unit Measure'],
    edge_items_per_uom: ['# Items/Buy Unit'], par_level: ['Minimum Level'],
    min_order_qty: ['Reorder Quantity'], is_serialized: ['Track Serial Number'],
    is_inactive: ['Inactive Item'], brand: ['Brand'],
  },
};

export function onlyWhatTheFileHas(payload, row, columns) {
  const out = {};
  for (const [field, value] of Object.entries(payload)) {
    const from = columns && columns[field];
    if (from && !from.some(h => Object.prototype.hasOwnProperty.call(row, h))) continue;
    out[field] = value;
  }
  return out;
}

async function existingItems(source) {
  const { data } = await fetchAll(() => supabase.from('items').select('*').eq('source', source).order('item_number'));
  return new Map((data || []).map(r => [source + '|' + r.item_number.toUpperCase(), r]));
}

async function matchNote() {
  try { return await runMatchingPass(supabase); }
  catch (err) { return ' (matching pass failed: ' + err.message + ')'; }
}
